import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { apiService } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { ZeugnisCreatePreview } from '../types';
import { apiErrorMessage } from '../utils/apiError';

interface KalotteCertDialogProps {
  chargeId: number;
  chargeNumber: string;
  open: boolean;
  onClose: () => void;
  onRecorded?: () => void;
}

function formatIssuedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const KalotteCertDialog: React.FC<KalotteCertDialogProps> = ({
  chargeId,
  chargeNumber,
  open,
  onClose,
  onRecorded,
}) => {
  const { isKalotteUser } = useAuth();
  const [vorlage, setVorlage] = useState<'kunde' | 'zoll_eu'>('kunde');
  const [format, setFormat] = useState<'xlsx' | 'pdf'>('pdf');
  const [lieferschein, setLieferschein] = useState<string>('');
  const [temporary, setTemporary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ZeugnisCreatePreview | null>(null);

  useEffect(() => {
    if (!open) return;
    setVorlage('kunde');
    setFormat('pdf');
    setTemporary(false);
    setLieferschein('');
    setPreview(null);
    let cancelled = false;
    apiService
      .zeugnisCreatePreview(chargeId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch(() => {
        if (!cancelled) {
          setPreview({ issued_at: null, zeugnis_number: null, values_changed: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, chargeId]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { blob, filename } = await apiService.downloadKalotteCert(
        chargeId,
        isKalotteUser ? format : 'pdf',
        lieferschein.trim() || undefined,
        vorlage,
        temporary
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (!temporary) onRecorded?.();
      onClose();
    } catch (err: any) {
      const fallback = 'Fehler beim Erzeugen des Zeugnisses. Bitte erneut versuchen.';
      if (err?.response?.data instanceof Blob) {
        // responseType=blob: the JSON error body arrives as a Blob.
        try {
          const parsed = JSON.parse(await err.response.data.text());
          setError(apiErrorMessage({ response: { data: parsed } }, fallback));
        } catch {
          setError(fallback);
        }
      } else {
        setError(apiErrorMessage(err, fallback));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Kalotte-Zeugnis erstellen für Charge {chargeNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <FormControl>
              <FormLabel>Vorlage</FormLabel>
              <RadioGroup
                value={vorlage}
                onChange={(e) => setVorlage(e.target.value as 'kunde' | 'zoll_eu')}
              >
                <FormControlLabel
                  value="kunde"
                  control={<Radio />}
                  label="Kundenzertifikat"
                  disabled={loading}
                />
                <FormControlLabel
                  value="zoll_eu"
                  control={<Radio />}
                  label="Zollmaterialzertifikat EU"
                  disabled={loading}
                />
              </RadioGroup>
              {vorlage === 'zoll_eu' && (
                <FormHelperText>
                  Schmelzland und Schmelznummer müssen am Lieferanten Zertifikat stehen.
                </FormHelperText>
              )}
            </FormControl>
            {preview === null && <CircularProgress size={18} />}
            {preview && preview.values_changed && !temporary && (
              <Alert severity="warning">
                Die bescheinigten Werte haben sich geändert. Erstellen zeichnet ein neues Zeugnis
                mit einer neuen Nummer auf. Das bisherige bleibt in der Historie.
              </Alert>
            )}
            {preview && !preview.values_changed && preview.issued_at && !temporary && (
              <Alert severity="info">
                Ausgestellt am {formatIssuedAt(preview.issued_at)}. Ein erneutes Erstellen verwendet
                dieses Zeugnis.
              </Alert>
            )}
            {preview && !preview.issued_at && !preview.values_changed && !temporary && (
              <Alert severity="warning">
                Mit Erstellen wird das Zeugnis aufgezeichnet. Die Nummer und die Werte bleiben ab
                dann bestehen.
              </Alert>
            )}
            {temporary && (
              <Alert severity="info">
                Diese Datei wird nicht aufgezeichnet. Sie trägt den Stempel VORLÄUFIG und erhält
                keine Zeugnisnummer.
              </Alert>
            )}
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mx: 0 }}
              control={
                <Checkbox
                  checked={temporary}
                  onChange={(event) => setTemporary(event.target.checked)}
                  disabled={loading}
                  sx={{ pt: 0 }}
                />
              }
              label="Nur vorläufig, nicht aufzeichnen"
            />
            <FormControl>
              <RadioGroup
                value={format}
                onChange={(e) => setFormat(e.target.value as 'xlsx' | 'pdf')}
              >
                <FormControlLabel
                  value="pdf"
                  control={<Radio />}
                  label="PDF — direkt drucken"
                  disabled={loading}
                />
                {isKalotteUser && (
                  <FormControlLabel
                    value="xlsx"
                    control={<Radio />}
                    label="Excel (.xlsx) — zum Bearbeiten und Drucken"
                    disabled={loading}
                  />
                )}
              </RadioGroup>
            </FormControl>
            <TextField
              label="Lieferschein (optional)"
              value={lieferschein}
              onChange={(e) => setLieferschein(e.target.value)}
              placeholder="aus Auftrag Lexware entnehmen"
              fullWidth
              disabled={loading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? 'Wird erstellt...' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {error !== null ? (
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default KalotteCertDialog;
